import {
  Habit,
  ID,
  RepeatingTask,
  ScheduleEntryState,
  Task,
  TaskType,
  Timestamp,
  UpdateTask,
} from '@griply/types';

import {
  completeTaskForToday,
  CompleteTaskForTodayResultKind,
} from './complete-task-for-today';
import { computeHabitEntryUpdate } from './compute-habit-entry-update';
import {
  getHabitTrackingTransitions,
  TaskCompletionTransition,
} from './get-habit-tracking-transitions';
import { countedGoalIds } from './links/counted-goal-ids';
import { getScheduleEntryStateForDate } from './schedules/get-schedule-entry-state-for-date';

export enum TaskCompletionActionKind {
  UpdateTask = 'update-task',
  UpdateHabitSchedules = 'update-habit-schedules',
  UpdateGoalTaskCount = 'update-goal-task-count',
  OpenManualEntry = 'open-manual-entry',
  TrackTaskComplete = 'track-task-complete',
  TrackTaskTransition = 'track-task-transition',
}

export type TaskCompletionAction =
  | {
      kind: TaskCompletionActionKind.UpdateTask;
      update: UpdateTask;
    }
  | {
      kind: TaskCompletionActionKind.UpdateHabitSchedules;
      updates: UpdateTask[];
    }
  | {
      kind: TaskCompletionActionKind.UpdateGoalTaskCount;
      goalId: ID;
      delta: 1 | -1;
    }
  | {
      kind: TaskCompletionActionKind.OpenManualEntry;
      habitId: ID;
      date: Timestamp;
    }
  | {
      kind: TaskCompletionActionKind.TrackTaskComplete;
      taskType: TaskType;
    }
  | {
      kind: TaskCompletionActionKind.TrackTaskTransition;
      taskType: TaskType;
      transition: TaskCompletionTransition;
    };

export type ResolveTaskCompletionActionsOptions = {
  today: Timestamp;
  type?: ScheduleEntryState;
  value?: number;
  increment?: number;
  referenceDate?: Timestamp;
  toggleDayEntry?: boolean;
};

export const resolveTaskCompletionActions = (
  task: Task,
  options: ResolveTaskCompletionActionsOptions,
): TaskCompletionAction[] => {
  const {
    today,
    type = ScheduleEntryState.Complete,
    value,
    increment,
    referenceDate,
    toggleDayEntry,
  } = options;

  const result = completeTaskForToday(task, {
    today,
    type,
    referenceDate,
    value,
    increment,
    toggleDayEntry,
  });

  if (result.kind === CompleteTaskForTodayResultKind.ManualEntryRequired) {
    return [
      {
        kind: TaskCompletionActionKind.OpenManualEntry,
        habitId: task.id,
        date: referenceDate ?? today,
      },
    ];
  }

  if (result.kind === CompleteTaskForTodayResultKind.Ignored) {
    return [];
  }

  if (task.type === TaskType.Task) {
    const becomingComplete = !task.completedAt;
    const actions: TaskCompletionAction[] = [
      {
        kind: TaskCompletionActionKind.UpdateTask,
        update: result.update,
      },
    ];

    countedGoalIds(task).forEach((goalId) =>
      actions.push({
        kind: TaskCompletionActionKind.UpdateGoalTaskCount,
        goalId,
        delta: becomingComplete ? 1 : -1,
      }),
    );

    if (becomingComplete) {
      actions.push({
        kind: TaskCompletionActionKind.TrackTaskComplete,
        taskType: task.type,
      });
    }
    if (!becomingComplete) {
      actions.push({
        kind: TaskCompletionActionKind.TrackTaskTransition,
        taskType: task.type,
        transition: TaskCompletionTransition.Uncomplete,
      });
    }

    return actions;
  }

  const habitDate = referenceDate ?? new Date();
  const priorEntryState = getScheduleEntryStateForDate(
    task as Habit | RepeatingTask,
    habitDate,
    today,
  );
  const { newSchedules, shouldCompleteHabit, shouldUncompleteHabit } =
    computeHabitEntryUpdate(task as Habit | RepeatingTask, habitDate, {
      today,
      type,
      value,
      increment,
      toggle: toggleDayEntry,
    });

  const actions: TaskCompletionAction[] = [
    {
      kind: TaskCompletionActionKind.UpdateHabitSchedules,
      updates: [{ id: task.id, schedules: newSchedules }],
    },
  ];

  // Subtask resets go in their own write so an un-writable (e.g. stale) child
  // can't roll back the habit schedule write through a shared atomic batch.
  if (task.childIds?.length) {
    actions.push({
      kind: TaskCompletionActionKind.UpdateHabitSchedules,
      updates: task.childIds.map((id) => ({ id, completedAt: null })),
    });
  }

  if (shouldCompleteHabit) {
    actions.push({
      kind: TaskCompletionActionKind.TrackTaskComplete,
      taskType: task.type,
    });
  }

  getHabitTrackingTransitions({
    priorState: priorEntryState,
    type,
    shouldUncompleteHabit,
  }).forEach((transition) => {
    actions.push({
      kind: TaskCompletionActionKind.TrackTaskTransition,
      taskType: task.type,
      transition,
    });
  });

  return actions;
};
